import React, { useEffect } from 'react';
import { useAudioPlayer } from '../../contexts/AudioPlayerContext';
import { PlayButton } from '../PlayButton/PlayButton';
import { XIcon } from '../icons';
import './MiniPlayer.css';

const MINI_PLAYER_HEIGHT = 64; // Height of the mini player in pixels

/**
 * MiniPlayer - A small persistent player that appears below the header when audio is playing
 * Shows track info, play/pause button with progress, and close button
 */
export const MiniPlayer: React.FC = () => {
  const { state, toggle, stop } = useAudioPlayer();

  // Set CSS variable for mini player offset when visible
  useEffect(() => {
    if (state.currentUrl) {
      document.documentElement.style.setProperty('--mini-player-offset', `${MINI_PLAYER_HEIGHT}px`);
    } else {
      document.documentElement.style.setProperty('--mini-player-offset', '0px');
    }

    return () => {
      document.documentElement.style.setProperty('--mini-player-offset', '0px');
    };
  }, [state.currentUrl]);

  // Don't render if no track is loaded
  if (!state.currentUrl) {
    return null;
  }

  // Extract track name from SoundCloud URL
  const getTrackName = (url: string): string => {
    try {
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/').filter(Boolean);
      // SoundCloud URLs are typically: /artist/track-name
      if (pathParts.length >= 2) {
        // Convert hyphenated track name to readable format
        const trackName = pathParts[pathParts.length - 1]
          .replace(/-/g, ' ')
          .replace(/\b\w/g, c => c.toUpperCase());
        return trackName;
      }
      return 'Now Playing';
    } catch {
      return 'Now Playing';
    }
  };

  const getArtistName = (url: string): string => {
    try {
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/').filter(Boolean);
      if (pathParts.length >= 1) {
        // First part is usually the artist
        const artistName = pathParts[0]
          .replace(/-/g, ' ')
          .replace(/\b\w/g, c => c.toUpperCase());
        return artistName;
      }
      return 'SoundCloud';
    } catch {
      return 'SoundCloud';
    }
  };

  const handlePlayPause = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    toggle(state.currentUrl!);
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    stop();
  };

  const trackName = getTrackName(state.currentUrl);
  const artistName = getArtistName(state.currentUrl);

  return (
    <div className="mini-player">
      <div className="mini-player__content">
        <PlayButton
          size={40}
          isPlaying={state.isPlaying}
          isLoading={state.isLoading}
          progress={state.progress}
          onClick={handlePlayPause}
          className="mini-player__play-btn"
        />

        <div className="mini-player__info">
          <span className="mini-player__track-name">{trackName}</span>
          <span className="mini-player__artist-name">{artistName}</span>
        </div>

        <button
          className="mini-player__close-btn"
          onClick={handleClose}
          aria-label="Stop playback"
          type="button"
        >
          <XIcon size="s" tone="darkGrey" />
        </button>
      </div>

      {/* Progress bar at bottom */}
      <div className="mini-player__progress-bar">
        <div
          className="mini-player__progress-fill"
          style={{ width: `${state.progress}%` }}
        />
      </div>
    </div>
  );
};

export default MiniPlayer;
